import {
  Button,
  IconButton,
  SvgRecord,
  SvgVideoCam,
  TextButton,
} from '@loomhq/lens';
import React, { Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import uaParser from 'ua-parser-js';

import { SDK_BROWSER_COMPATIBILITY_TEST_FAILED } from 'constants/events';
import { VIDEO } from 'constants/ssr';
import { EDUCATION as WORKSPACE_PERSONA_EDUCATION } from 'constants/useCasePlans';
import { WORKSPACE_PLAN_EDUCATION } from 'constants/workspacePlans';
import { selectVideoId } from 'selectors/shareVideo';
import { selectUserId } from 'selectors/user';
import { selectRecordReplyEnabled } from 'selectors/video';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';
import { isSdkSupported as importedIsSdkSupported } from 'utilities/recordSdk/isSdkSupported';

import useMedia from 'hooks/useMedia';

const TextIconButton = props => {
  const { compact, textButtonSize = 'medium', buttonText } = props;

  return compact ? (
    <IconButton isDisabled icon={<SvgVideoCam />} />
  ) : (
    <div className="px:xsmall">
      <TextButton isDisabled icon={<SvgVideoCam />} size={textButtonSize}>
        {buttonText}
      </TextButton>
    </div>
  );
};

export const RecordReplyWrapper = props => {
  const recordReplyEnabled = useSelector(selectRecordReplyEnabled);
  const userId = useSelector(selectUserId);
  const videoId = useSelector(selectVideoId);
  const [isSdkSupported, setIsSdkSupported] = useState(false);
  const [sdkNotSupportedReason, setSdkNotSupportedReason] = useState();

  const fullSize = useMedia(['(min-width: 560px)'], [true], false);
  let textButtonSize = fullSize ? 'medium' : 'small';

  if (props.forceSmallIcon) {
    textButtonSize = 'small';
  }

  useEffect(() => {
    (async () => {
      const { supported, error } = await importedIsSdkSupported();

      if (!supported) {
        const parser = new uaParser();
        const uaDetails = parser.getResult();

        analytics.track(SDK_BROWSER_COMPATIBILITY_TEST_FAILED, {
          userId,
          videoId,
          browserName: uaDetails?.browser?.name,
          browserVersion: uaDetails?.browser?.version,
          osName: uaDetails?.os?.name,
          osVersion: uaDetails?.os?.version,
          sdkNotSupportedReason,
          userAgent: uaDetails?.ua,
        });
      }

      setIsSdkSupported(supported);
      setSdkNotSupportedReason(error);
    })();
  }, []);

  /**
   * When a user lands on the share page after recording a video the video workspace persona and plan
   * is available in redux, but is then removed after a subsuequent API call that doesn't return
   * this information. So opting here to use to video data injected into the template rather
   * than redux.
   */
  const { videoWorkspacePersona, videoWorkspacePlan } = window[VIDEO];
  const isEDUVideo =
    videoWorkspacePersona?.persona_v1?.use_case_plan ===
      WORKSPACE_PERSONA_EDUCATION ||
    videoWorkspacePlan === WORKSPACE_PLAN_EDUCATION;

  const experimentAllowed =
    recordReplyEnabled && isSdkSupported && !isEDUVideo && !isMobile;

  if (experimentAllowed) {
    return (
      <RecordReplyFallbackButton {...props} textButtonSize={textButtonSize} />
    );
  }

  return null;
};

export const RecordReplyWrapperMemo = React.memo(RecordReplyWrapper);

const RecordReplyFallbackButton = props => {
  const LazyRecordAReply = React.lazy(() => import('./record-reply'));

  const {
    iconButton,
    compact,
    setHasRecordReply = () => {},
    textButtonSize,
    buttonText = 'Record a Reply',
  } = props;

  useEffect(() => {
    setHasRecordReply(true);
  }, [setHasRecordReply]);

  return (
    <Suspense
      fallback={
        // Render a fake button as a fallback
        iconButton ? (
          <TextIconButton
            compact={compact}
            textButtonSize={textButtonSize}
            buttonText={buttonText}
          />
        ) : (
          <div className="bgc:white radius:medium theme-light">
            <Button
              variant="neutral"
              icon={<SvgRecord color="#ff623e" />}
              isDisabled
            >
              {buttonText}
            </Button>
          </div>
        )
      }
    >
      <LazyRecordAReply iconButton={iconButton} compact={compact} {...props} />
    </Suspense>
  );
};

export default RecordReplyFallbackButton;
